import React, { useState, useEffect } from 'react';
import { 
  ExclamationTriangleIcon, 
  ChartBarIcon, 
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  BellIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../auth/AuthContext';
import api from '../services/api';

export default function SafetyMonitoring() {
  const { user } = useAuth();
  const [adverseEvents, setAdverseEvents] = useState([]);
  const [safetySignals, setSafetySignals] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeverity, setSelectedSeverity] = useState('all');

  useEffect(() => {
    loadSafetyData();
  }, []);

  const loadSafetyData = async () => {
    try {
      const [eventsResponse, signalsResponse, reportsResponse] = await Promise.all([
        api.get('/health/safety/adverse-events'),
        api.get('/health/safety/signals'),
        api.get('/health/safety/reports')
      ]);
      
      setAdverseEvents(eventsResponse.data);
      setSafetySignals(signalsResponse.data);
      setReports(reportsResponse.data);
    } catch (error) {
      console.error('Failed to load safety data:', error);
    } finally {
      setLoading(false);
    }
  };

  const severityLevels = [
    { id: 'fatal', name: 'Fatal', color: 'red', count: adverseEvents.filter(ae => ae.severity === 'fatal').length },
    { id: 'life_threatening', name: 'Life-threatening', color: 'orange', count: adverseEvents.filter(ae => ae.severity === 'life_threatening').length },
    { id: 'serious', name: 'Serious', color: 'yellow', count: adverseEvents.filter(ae => ae.severity === 'serious').length },
    { id: 'moderate', name: 'Moderate', color: 'blue', count: adverseEvents.filter(ae => ae.severity === 'moderate').length },
    { id: 'mild', name: 'Mild', color: 'green', count: adverseEvents.filter(ae => ae.severity === 'mild').length }
  ];

  const filteredEvents = selectedSeverity === 'all' 
    ? adverseEvents 
    : adverseEvents.filter(event => event.severity === selectedSeverity);

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'fatal':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'life_threatening':
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />;
      case 'serious':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <BellIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Safety Monitoring</h1>
        <p className="mt-2 text-gray-600">
          Monitor adverse events, safety signals, and regulatory reporting requirements
        </p>
      </div>

      {/* Safety Dashboard Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total AEs</p>
              <p className="text-2xl font-bold text-gray-900">{adverseEvents.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Serious AEs</p>
              <p className="text-2xl font-bold text-gray-900">
                {adverseEvents.filter(ae => ['fatal', 'life_threatening', 'serious'].includes(ae.severity)).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <BellIcon className="h-8 w-8 text-yellow-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Safety Signals</p>
              <p className="text-2xl font-bold text-gray-900">{safetySignals.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Reports Filed</p>
              <p className="text-2xl font-bold text-gray-900">{reports.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Severity Filter */}
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Adverse Events by Severity</h3>
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setSelectedSeverity('all')}
            className={`px-3 py-2 rounded-md text-sm font-medium ${
              selectedSeverity === 'all'
                ? 'bg-red-100 text-red-800'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Severities ({adverseEvents.length})
          </button>
          {severityLevels.map((severity) => (
            <button
              key={severity.id}
              onClick={() => setSelectedSeverity(severity.id)}
              className={`px-3 py-2 rounded-md text-sm font-medium ${
                selectedSeverity === severity.id
                  ? `bg-${severity.color}-100 text-${severity.color}-800`
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {severity.name} ({severity.count})
            </button>
          ))}
        </div>

        {/* Events List */}
        <div className="divide-y divide-gray-200">
          {filteredEvents.slice(0, 10).map((event) => (
            <div key={event.id} className="py-4">
              <div className="flex items-start space-x-3">
                {getSeverityIcon(event.severity)}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900">{event.event_term}</h4>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      event.severity === 'fatal' 
                        ? 'bg-red-100 text-red-800'
                        : event.severity === 'life_threatening'
                        ? 'bg-orange-100 text-orange-800'
                        : event.severity === 'serious'
                        ? 'bg-yellow-100 text-yellow-800'
                        : event.severity === 'moderate'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {event.severity.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                  <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                    <span>Study: {event.study_id}</span>
                    <span>Subject: {event.subject_id}</span>
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {new Date(event.onset_date).toLocaleDateString()}
                    </div>
                    <span className={`inline-flex items-center ${
                      event.causality === 'related' 
                        ? 'text-red-600' 
                        : event.causality === 'possibly_related'
                        ? 'text-yellow-600'
                        : 'text-gray-600'
                    }`}>
                      {event.causality.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {filteredEvents.length === 0 && (
            <div className="py-8 text-center text-gray-500">
              No adverse events found for selected severity
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Safety Signals */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Active Safety Signals</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {safetySignals.map((signal) => (
              <div key={signal.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{signal.signal_description}</h4>
                    <p className="text-sm text-gray-500">Products: {signal.products.join(', ')}</p>
                    <div className="flex items-center mt-1 text-xs text-gray-400">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      Detected: {new Date(signal.detection_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      signal.priority === 'high' 
                        ? 'bg-red-100 text-red-800'
                        : signal.priority === 'medium'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {signal.priority} priority
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      signal.status === 'under_review' 
                        ? 'bg-blue-100 text-blue-800'
                        : signal.status === 'confirmed'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {signal.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {safetySignals.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-500">
                <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-2" />
                No active safety signals
              </div>
            )}
          </div>
        </div>

        {/* Regulatory Reports */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Regulatory Reports</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {reports.map((report) => (
              <div key={report.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{report.report_type}</h4>
                    <p className="text-sm text-gray-500">Report ID: {report.report_number}</p>
                    <div className="flex items-center mt-1 text-xs text-gray-400">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      Submitted: {new Date(report.submission_date).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      report.status === 'submitted' 
                        ? 'bg-green-100 text-green-800'
                        : report.status === 'acknowledged'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {report.status}
                    </span>
                    <span className="text-xs text-gray-500">
                      to {report.regulatory_authority}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            {reports.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-500">
                No regulatory reports filed
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reporting Timeline */}
      <div className="mt-8 bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Upcoming Reporting Deadlines</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
            <div>
              <h4 className="text-sm font-medium text-red-800">Expedited SAE Report</h4>
              <p className="text-sm text-red-600">Fatal case - 7 day deadline</p>
            </div>
            <span className="text-sm font-medium text-red-800">Due in 2 days</span>
          </div>
          <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
            <div>
              <h4 className="text-sm font-medium text-yellow-800">PSUR Submission</h4>
              <p className="text-sm text-yellow-600">Periodic safety update report</p>
            </div>
            <span className="text-sm font-medium text-yellow-800">Due in 15 days</span>
          </div>
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
            <div>
              <h4 className="text-sm font-medium text-green-800">Annual Safety Report</h4>
              <p className="text-sm text-green-600">Comprehensive safety review</p>
            </div>
            <span className="text-sm font-medium text-green-800">Due in 45 days</span>
          </div>
        </div>
      </div>
    </div>
  );
}